import { useSyncExternalStore } from "react";
import { getModalState, subscribeToModalState, resolveModal, closeModal } from "./store";
import { ENTITY_MODAL_REGISTRY } from "./registry";

/**
 * Single global host for entity create/edit panels opened via
 * openModal(toolName) (see store.js). Mounted once in RootLayout, mirroring
 * where the old GlobalModals.js used to live - see
 * docs/futurescope/ai-tool-calls-spec.md (simplifyserver) for why this
 * generalizes useQuickCreateModals rather than resurrecting the removed
 * ModalContext registry.
 *
 * Renders every entry in the store's stack (not just one) - a picker's own
 * inline-create footer opened FROM inside an already-open panel (see
 * store.js's doc comment) pushes a second entry rather than replacing the
 * first, so both need to stay mounted simultaneously. Rendered in stack
 * order (oldest first): every ModalShell-based panel here uses the same
 * fixed overlay z-index ($z-overlay), so with no z-index difference
 * between them, later DOM siblings simply paint over earlier ones at that
 * shared level - the most-recently-opened (last in the array) panel
 * naturally ends up on top, dimming everything opened before it, exactly
 * the expected "stacked dialogs" look. `useEscapeKey`'s own topmost-only
 * fix (see its doc comment) keeps Escape closing just the top panel
 * instead of every open one at once.
 */
const ModalHost = () => {
  const stack = useSyncExternalStore(subscribeToModalState, getModalState);

  return stack.map((entry) => {
    const registryEntry = ENTITY_MODAL_REGISTRY[entry.toolName];
    if (!registryEntry) return null;

    const Panel = registryEntry.component;
    return (
      <Panel
        key={entry.id}
        isOpen
        mode={registryEntry.mode}
        rowData={entry.params?.rowData}
        onClose={() => closeModal(entry.id)}
        onSuccess={(result) => resolveModal(entry.id, result)}
        {...entry.params?.modalProps}
      />
    );
  });
};

export default ModalHost;
